import React from 'react';
import styled from 'styled-components';
import { I18n } from 'react-redux-i18n';
import { Tooltip } from 'antd';

import Pills from 'components/Pills';
import Row from 'components/List/Row';
import Pellet from 'components/Pellet';
import Icon from 'components/Icon';
import Button from 'components/Button';
import SelectFilter from 'components/Select/SelectFilter';
import SearchMultipleFields from 'components/SearchMultipleFields';

import { getStateToStatus } from 'utils/pos';

const isMobile = window.innerWidth <= 845;

const rowGap = 13;
const buttonRemoveRestaurantLength = 16;
const pelletStatusRestaurantLength = 7;

const Container = styled.div`
  max-width: 380px;
  height: 100%;
  border-radius: 4px;
  box-shadow: 0 10px 30px 1px rgba(137, 137, 137, 0.25);
  background-color: ${(props) => props.theme.colors['white']};

  @media (max-width: ${(props) =>
      props.theme.devices.isTablet ? '0px' : '845px'}) {
    max-width: 100%;
    box-shadow: none;
  }
`;

const List = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  margin: 0 20px 20px;

  @media (max-width: ${(props) =>
      props.theme.devices.isTablet ? '0px' : '845px'}) {
    height: 45vh;
  }
`;

const Header = styled.div`
  padding: 20px 20px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (max-width: ${(props) =>
      props.theme.devices.isTablet ? '0px' : '845px'}) {
    padding: 0 20px 20px 20px;
  }

  .search {
    @media (max-width: ${(props) =>
        props.theme.devices.isTablet ? '0px' : '845px'}) {
      display: none;
    }
  }

  .grid {
    grid-template-columns: 1fr 1fr;
    display: grid;
    align-items: center;
    margin: 0;
  }

  .title {
    color: ${(props) => props.theme.colors['terra-cotta']};
    font-family: ${(props) => props.theme.fonts.oswald};
  }

  .restoreButtonContainer {
    margin-left: auto;
  }
`;

const Wrapper = styled.div`
  height: 100%;
  overflow: hidden;
  display: grid;
  grid-template-columns: 100%;

  h1 {
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    font-size: 22px;
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    line-height: 1.09;
    letter-spacing: normal;
    color: #212529;
  }

  h2 {
    font-family: ${(props) => props.theme.fonts.oswald};
    font-size: 12px;
    font-weight: 400;
    font-style: normal;
    font-stretch: normal;
    line-height: normal;
    letter-spacing: normal;
    color: ${(props) => props.theme.colors['dark']};
    text-transform: uppercase;
  }

  .close {
    width: 16px;
    height: 16px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    background-color: rgba(20, 29, 38, 0.12);
    color: rgba(39, 53, 68, 0.5);
    font-size: 12px;
  }

  .close:hover {
    cursor: pointer;
  }

  .close > .icon {
    width: 12px;
    height: 12px;
    color: #2b3d54;
  }

  .removeContainer:hover {
    cursor: pointer;
  }

  .row {
    display: flex;
    flex-direction: row;
    margin: 0 ${rowGap}px;
    column-gap: ${rowGap}px;
    color: ${(props) => props.theme.colors['dark-two']};
  }

  .row:hover {
    cursor: pointer;
  }
`;

const RowTitle = styled.div`
  opacity: 0.9;
  font-family: ${(props) => props.theme.fonts.sfcompact_text};
  font-size: 12px;
  font-weight: 400;
  font-style: normal;
  font-stretch: normal;
  line-height: 2;
  letter-spacing: normal;
  color: ${(props) => props.theme.colors['dark']};
`;

const RowLocation = styled.div`
  color: rgba(39, 53, 68, 0.5);
  font-weight: 400;
  font-style: normal;
  font-stretch: normal;
  line-height: 2;
  letter-spacing: normal;
  font-size: 12px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  font-family: ${(props) => props.theme.fonts.sfcompact_text};
`;

const renderRow = (contentRow, removeRow, handleSelectMarker) =>
  contentRow.map((data, index) => {
    // fixed for text collapse on adress
    const letterSize = isMobile ? 8 : 7;
    let nameLength = data.name.length * letterSize;
    // fix variables of length letters like (w or l) 1 letter, differents sizes
    if (data.name.length > 20) nameLength = nameLength - 15;
    else if (data.name.length > 15) nameLength = nameLength - 10;

    const margin = !isMobile ? 13 * 2 : 0;
    const lengthAdress =
      330 - // 365 fixed width of list
      margin - // margin left/right of list -- no margin on mobile
      13 * 3 - // colums-gap between all elements (4 elements => 3 gaps)
      nameLength - // less lenght of name of pos div
      buttonRemoveRestaurantLength - // less button remove with cross icon
      pelletStatusRestaurantLength; // less pellet status pos

    return (
      <Row dark={index % 2 === 0} key={`row-${index}`}>
        <div className="row" onClick={() => handleSelectMarker(data)}>
          <div
            className="close"
            onClick={(e) => {
              e.stopPropagation();
              removeRow(data.store_id);
            }}
          >
            <Icon icon="close" />
          </div>
          <RowTitle style={{ width: nameLength }}>{data.name}</RowTitle>
          <Pellet
            value={getStateToStatus(data.state)}
            square={data.network_code === 'OFF' ? true : undefined}
          />
          <Tooltip placement="right" title={data.address}>
            <RowLocation style={{ width: lengthAdress }}>
              {data.address}
            </RowLocation>
          </Tooltip>
        </div>
      </Row>
    );
  });

const PointsOfSellsList = ({
  optionsPills,
  handleChangeStatusPill,
  handleChangeTypePill,
  contentRow,
  removeRow,
  searchable,
  handleInputChange,
  resetExcluded,
  handleSelectMarker,
  searchValue,
  wines,
  optionsWine,
  hasWineshopAccess,
  isBranded,
  onChangeWines
}) => (
  <Container>
    <Wrapper>
      <Header>
        <div className="grid">
          <div className="title">{I18n.t('travel.details').toUpperCase()}</div>
          <div className="restoreButtonContainer">
            <Button
              onClick={() => resetExcluded()}
              label={I18n.t('travel.citiesList.restore')}
              borderRadius="25px"
              color="dark"
              height="28px"
              width="110px"
              textTransform="normal"
              fontSize="12px"
            />
          </div>
        </div>
        <SelectFilter
          isMulti
          placeholder={I18n.t('placeholderAll')}
          selectedOption={wines}
          onChange={(selectedOption) => onChangeWines(selectedOption)}
          options={optionsWine ? optionsWine : []}
          label={
            isBranded
              ? I18n.t('brandsMaj')
              : I18n.t('travel.citiesList.wineLabel')
          }
          dropdownProps={{
            styles: `margin-left:-25px`
          }}
        />
        {hasWineshopAccess && (
          <div>
            <Pills
              options={optionsPills.type}
              onChange={({ value }) => handleChangeTypePill(value)}
              styles={`
              justify-content: space-between;
              gap: 8px;
            `}
            />
          </div>
        )}
        <div>
          <Pills
            styles={{
              justifyContent: 'space-between',
              width: isMobile ? 'calc(100vw - 82px)' : 321
            }}
            options={optionsPills.status}
            onChange={({ value }) => handleChangeStatusPill(value)}
          />
        </div>
        {!isMobile && (
          <SearchMultipleFields
            customSearchFieldStyles={{
              control: { backgroundColor: 'white' }
            }}
            className="search"
            minOpen={0}
            options={searchable}
            inputValue={searchValue}
            placeholder={I18n.t('travel.citiesList.searchPlaceholder')}
            handleInputChange={(value) => handleInputChange(value)}
          />
        )}
      </Header>
      <List>{renderRow(contentRow, removeRow, handleSelectMarker)}</List>
    </Wrapper>
  </Container>
);

export default PointsOfSellsList;
